import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useCallback, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {FormBuilderContent} from 'src/app/form-builder/components/FormBuilderContent';
import {
  SectionValidationProvider,
  useSectionValidation,
} from 'src/shared/hooks/SectionValidationContext';
import {
  useGetFieldList,
  useGetFormSettings,
} from 'src/app/form-builder/hooks/useFormBuilder';
import {
  FormBuilderProvider,
  initialFormSettings,
} from 'src/app/form-builder/context/FormBuilderContext';
import {formBuilderRegistry} from 'src/app/form-builder/formBuilderRegistry';
import {SidebarLinkForms} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useFormsBreadcrumbs} from 'src/shared/hooks/useFormsBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {mapFormSettingsV2ToFormDefinitionRequest} from 'src/types/FormV2Mapping';
import {useCreateFormDefinitionMutation} from 'src/redux/queries/formDefinition';
import {
  FormBuilderDocumentTitle,
  buildSectionValidationMessage,
} from '../formBuilderHelpers';
import {CreateConfirmModal} from './CreateConfirmModal';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const EditFormBuilderPageContent: React.FC = () => {
  const formSettings = useGetFormSettings();
  const formFields = useGetFieldList();
  const {validateAll} = useSectionValidation();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);
  const [showAfterCreateModal, setShowAfterCreateModal] = useState(false);
  const [createdFormId, setCreatedFormId] = useState<number | undefined>();

  const perms = useModulePermissions(ModuleEnum.Forms);

  const [createFormDefinition] = useCreateFormDefinitionMutation();

  const afterCreate = useCallback((id: number) => {
    setCreatedFormId(id);
    setShowAfterCreateModal(true);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    const results = await validateAll();
    const message = buildSectionValidationMessage(results);
    if (message) {
      setAlert(message, 'error');
      return;
    }

    try {
      setIsSavePending(true);

      const body = mapFormSettingsV2ToFormDefinitionRequest(
        0,
        0,
        formSettings,
        formFields
      );

      const response = await createFormDefinition(body).unwrap();

      afterCreate(response.id);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  }, [
    validateAll,
    formSettings,
    formFields,
    afterCreate,
    setAlert,
    createFormDefinition,
  ]);

  const {goBack: handleCancel} = useBackNavigation(
    PathUtility.FormsDashboard('manage')
  );

  const {breadcrumbs, fallbackTo} = useFormsBreadcrumbs({
    mode: 'form-builder-create',
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
      <Stack gap="lg">
        <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
        <PageHeader>
          <PageHeader.Title icon={SidebarLinkForms.icon}>
            Create Form
          </PageHeader.Title>
          <PageHeader.Actions>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            {perms.create && (
              <PageHeader.Action
                variant="create"
                onClick={handleSubmit}
                loading={isSavePending}>
                Create
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <FormBuilderContent registry={formBuilderRegistry} />

        {perms.create && (
          <Group>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant="create"
              onClick={handleSubmit}
              loading={isSavePending}>
              Create
            </PageHeader.Action>
          </Group>
        )}
      </Stack>

      {showAfterCreateModal && createdFormId && (
        <CreateConfirmModal
          opened={showAfterCreateModal}
          formId={createdFormId}
          onClose={() => setShowAfterCreateModal(false)}
        />
      )}
    </ContentWrapper>
  );
};

export const EditFormBuilderPage: React.FC = () => {
  useSetDocumentTitle(FormBuilderDocumentTitle);

  return (
    <FormBuilderProvider
      initialFieldList={[]}
      initialFormSettings={initialFormSettings}>
      <SectionValidationProvider>
        <EditFormBuilderPageContent />
      </SectionValidationProvider>
    </FormBuilderProvider>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.FormBuilderCreate(),
  element: <EditFormBuilderPage />,
  module: ModuleEnum.Forms,
  operation: PermissionOperationEnum.Create,
  isPrivate: true,
});
